import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Package, Receipt, Users, HandCoins, Settings as SettingsIcon, LogOut, Plus, KeyRound, Menu, X, UserRound, Ticket, Globe, Search, Layout, MessageCircle, CreditCard, Mail, Crown, Newspaper, Heart, Gem } from "lucide-react";
import { Button } from "../components/ui/button";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import CommandPalette from "../components/CommandPalette";

// Nav items support two shapes:
//   - link:   { to, label, icon, testId, end?, adminOnly? }
//   - header: { section: "分组名", adminOnly? }  → renders a section label divider
const nav = [
    { to: "/", label: "仪表板", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
    { to: "/products", label: "产品管理", icon: Package, testId: "nav-products" },
    { to: "/sales", label: "销售记录", icon: Receipt, testId: "nav-sales" },
    { to: "/customers", label: "客户管理", icon: UserRound, testId: "nav-customers" },
    { to: "/coupons", label: "优惠码", icon: Ticket, testId: "nav-coupons" },
    { to: "/vip", label: "VIP 会员", icon: Crown, testId: "nav-vip" },
    { to: "/insights", label: "资讯管理", icon: Newspaper, testId: "nav-insights" },
    { to: "/payouts", label: "分成记录", icon: HandCoins, testId: "nav-payouts" },

    { section: "落地页" },
    { to: "/site/home", label: "首页配置", icon: Layout, testId: "nav-site-home" },
    { to: "/site/info", label: "网站信息", icon: Globe, testId: "nav-site-info" },
    { to: "/site/whatsapp", label: "WhatsApp", icon: MessageCircle, testId: "nav-site-whatsapp" },
    { to: "/site/reviews", label: "顾客好评", icon: Heart, testId: "nav-site-reviews" },
    { to: "/crystals", label: "水晶管理", icon: Gem, testId: "nav-crystals" },

    { section: "系统", adminOnly: true },
    { to: "/users", label: "用户管理", icon: Users, testId: "nav-users", adminOnly: true },
    { to: "/settings", label: "常规设置", icon: SettingsIcon, testId: "nav-settings", adminOnly: true, end: true },
    { to: "/settings/payment", label: "Stripe 收款", icon: CreditCard, testId: "nav-settings-payment", adminOnly: true },
    { to: "/settings/email", label: "邮件通知", icon: Mail, testId: "nav-settings-email", adminOnly: true },
];

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [pwOpen, setPwOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Global Cmd+K / Ctrl+K shortcut
    useEffect(() => {
        const handler = (e) => {
            if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setPaletteOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

    useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = "hidden";
            return () => { document.body.style.overflow = ""; };
        }
    }, [sidebarOpen]);

    const doLogout = async () => {
        await logout();
        navigate("/login");
    };

    const isAdmin = user?.role === "admin";
    const visibleNav = nav.filter((n) => !n.adminOnly || isAdmin);
    const linkNav = visibleNav.filter((n) => n.to);
    const currentTitle = linkNav.find((n) => n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))?.label || "福缘库存管理";

    return (
        <div className="relative min-h-screen bg-gold-50">
            {/* Ambient glow so the glass sidebar has something to refract */}
            <div aria-hidden className="pointer-events-none fixed -left-24 top-1/4 h-[36rem] w-[36rem] rounded-full bg-gold-300/40 blur-[120px]" />
            <div aria-hidden className="pointer-events-none fixed -left-10 bottom-0 h-72 w-72 rounded-full bg-rose-200/35 blur-[100px]" />
            <div aria-hidden className="pointer-events-none fixed left-40 top-0 h-64 w-64 rounded-full bg-amber-100/60 blur-[90px]" />
            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-white/40 shadow-sm flex items-center gap-3 px-4 h-14">
                <button
                    data-testid="open-sidebar-btn"
                    onClick={() => setSidebarOpen(true)}
                    className="h-9 w-9 flex items-center justify-center rounded-md text-slate-700 hover:bg-slate-100"
                    aria-label="打开菜单"
                >
                    <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="h-7 w-7 rounded-md bg-gold-500 flex items-center justify-center shrink-0">
                        <Package className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm font-semibold text-slate-900 truncate">{currentTitle}</span>
                </div>
                <button
                    data-testid="open-palette-mobile-btn"
                    onClick={() => setPaletteOpen(true)}
                    className="h-9 w-9 flex items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                    aria-label="搜索"
                >
                    <Search className="h-4 w-4" />
                </button>
                <Button
                    data-testid="new-sale-mobile-btn"
                    size="sm"
                    onClick={() => navigate("/sales/new")}
                    className="bg-gold-600 hover:bg-gold-700 text-white gap-1 h-9 px-3"
                >
                    <Plus className="h-3.5 w-3.5" /> 销售
                </Button>
            </header>

            {/* Mobile overlay backdrop */}
            {sidebarOpen && (
                <button
                    aria-label="关闭菜单"
                    onClick={() => setSidebarOpen(false)}
                    className="lg:hidden fixed inset-0 z-40 bg-gold-900/25 backdrop-blur-md"
                    data-testid="sidebar-backdrop"
                />
            )}

            <aside
                data-testid="sidebar"
                className={`fixed inset-y-0 left-0 z-50 w-64 lg:w-60 lg:inset-y-4 lg:left-4 lg:h-[calc(100vh-2rem)] lg:rounded-[1.75rem] bg-gold-50/85 lg:bg-white/45 backdrop-blur-2xl backdrop-saturate-150 text-slate-800 flex flex-col border-r border-white/70 lg:border lg:border-white/70 shadow-[0_8px_40px_-12px_rgba(146,109,40,0.35)] ring-1 ring-gold-200/40 transform transition-transform duration-200 ease-out lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
            >
                <div className="px-5 py-4 lg:py-6 border-b border-gold-200/50 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center shadow-md shadow-gold-600/30">
                            <Package className="h-4 w-4 text-white" />
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-gold-600/80">FuYuan</div>
                            <div className="text-sm font-semibold leading-tight text-slate-900">福缘库存管理</div>
                        </div>
                    </div>
                    <button
                        data-testid="close-sidebar-btn"
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden h-8 w-8 flex items-center justify-center rounded-md text-gold-700 hover:bg-white/60"
                        aria-label="关闭菜单"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    <button
                        data-testid="open-palette-btn"
                        onClick={() => setPaletteOpen(true)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 mb-2 rounded-xl text-sm bg-white/55 hover:bg-white/80 text-gold-700 hover:text-gold-900 transition-colors border border-white/70 shadow-sm"
                    >
                        <Search className="h-3.5 w-3.5" />
                        <span className="flex-1 text-left text-xs">快速搜索...</span>
                        <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white/70 border border-gold-200/70 text-[10px] font-mono text-gold-600">⌘K</kbd>
                    </button>
                    {visibleNav.map((n, idx) => {
                        if (n.section) {
                            return (
                                <div
                                    key={`sec-${idx}`}
                                    className="pt-3 pb-1 px-3 text-[10px] font-semibold uppercase tracking-widest text-gold-600/70 select-none"
                                    data-testid={`nav-section-${n.section}`}
                                >
                                    {n.section}
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={n.testId}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 ${
                                        isActive
                                            ? "bg-white/85 text-gold-800 font-medium border border-white/90 shadow-sm shadow-gold-700/10"
                                            : "border border-transparent text-slate-600 hover:bg-white/55 hover:text-gold-800 hover:translate-x-0.5"
                                    }`
                                }
                            >
                                <n.icon className="h-4 w-4" />
                                {n.label}
                            </NavLink>
                        );
                    })}

                    <div className="pt-4">
                        <Button
                            data-testid="new-sale-btn"
                            onClick={() => navigate("/sales/new")}
                            className="w-full bg-gold-600/90 hover:bg-gold-700 text-white justify-start gap-2 h-10 rounded-xl shadow-md shadow-gold-700/20 backdrop-blur-sm"
                        >
                            <Plus className="h-4 w-4" /> 新建销售单
                        </Button>
                    </div>
                </nav>

                <div className="p-3 border-t border-gold-200/50">
                    <div className="px-3 py-2 mb-2">
                        <div className="text-xs text-gold-600/80 uppercase tracking-wider">已登录</div>
                        <div className="text-sm text-slate-900 font-medium truncate">{user?.name || user?.email}</div>
                        <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                    <button
                        data-testid="change-password-btn"
                        onClick={() => setPwOpen(true)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:bg-white/60 hover:text-gold-800 rounded-xl transition-colors duration-150"
                    >
                        <KeyRound className="h-4 w-4" />
                        修改密码
                    </button>
                    <button
                        data-testid="logout-btn"
                        onClick={doLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:bg-white/60 hover:text-gold-800 rounded-xl transition-colors duration-150"
                    >
                        <LogOut className="h-4 w-4" />
                        退出登录
                    </button>
                </div>
            </aside>

            <main className="lg:ml-[17rem] min-h-screen">
                <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8">
                    <Outlet />
                </div>
            </main>

            <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
            <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        </div>
    );
}
